import { FormField, FormFieldType } from '@enroller/admissions-components';
import { Trans, useLingui } from '@lingui/react/macro';
import { Alert, Checkbox, FormControl, FormControlLabel, Grid2, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { Control, useWatch } from 'react-hook-form';
import { useCountries } from '../contexts/CountryContext';
import { CourseReservation } from './CourseReservation';

interface Guardian2DetailsProps {
  control: Control<CourseReservation>;
  showAddress: boolean;
  onToggleAddress: (show: boolean) => void;
}

export const Guardian2Details = ({ control, showAddress, onToggleAddress }: Guardian2DetailsProps) => {
  const { t } = useLingui();
  const { countries, loading } = useCountries();
  const theme = useTheme();
  const soleGuardianWatch = useWatch({ control, name: 'guardian1SoleGuardian' });

  const toggleSameAddress = () => {
    onToggleAddress(!showAddress);
  };

  return (
    <Grid2 container spacing={3} id="guardian-2">
      <Grid2 size={12}>
        <Typography variant="subtitle1">
          <Trans>Parent / Guardian 2 Details</Trans>
        </Typography>
      </Grid2>
      {soleGuardianWatch === 'No' ? (
        <>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormField type={FormFieldType.Text} label={t`Parent / Guardian 2 first name`} name="guardian2FirstName" control={control} />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormField type={FormFieldType.Text} label={t`Parent / Guardian 2 last name`} name="guardian2LastName" control={control} />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormField
              type={FormFieldType.Phone}
              label={t`Parent / Guardian 2 phone`}
              name="guardian2Phone"
              control={control}
              placeholder={t`Enter phone number (including country code)`}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormField type={FormFieldType.Text} label={t`Parent / Guardian 2 email`} name="guardian2Email" control={control} />
          </Grid2>
          <Grid2 size={12}>
            <Typography variant="subtitle1">
              <Trans>Parent / Guardian 2 Address</Trans>
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControl>
              <FormControlLabel control={<Checkbox onClick={toggleSameAddress} />} label={t`Same as student's address`} />
            </FormControl>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }} sx={{ [theme.breakpoints.down('md')]: { display: 'none' } }}>
            {/* push 6 */}
          </Grid2>
          {showAddress && (
            <>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <FormField type={FormFieldType.Text} label={t`Street address`} name="guardian2StreetAddress" control={control} maxLength={40} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }} sx={{ [theme.breakpoints.down('md')]: { display: 'none' } }}>
                {/* push 6 */}
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <FormField type={FormFieldType.Text} label={t`City`} name="guardian2City" control={control} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <FormField type={FormFieldType.Text} label={t`State / Region / Province`} name="guardian2Region" control={control} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <FormField type={FormFieldType.Text} label={t`Postal / Zip code`} name="guardian2PostalCode" control={control} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                {loading || countries === undefined ? (
                  <Stack direction="column" justifyContent="flex-end" sx={{ height: '100%' }}>
                    <Skeleton variant="rectangular" width="100%" height="48px" />
                  </Stack>
                ) : (
                  <FormField type={FormFieldType.Dropdown} label={t`Country`} name="guardian2Country" options={countries} control={control} />
                )}
              </Grid2>
            </>
          )}
        </>
      ) : (
        <Grid2 size={12}>
          <Alert severity="info" variant="outlined">
            <Trans>As Parent / Guardian 1 is the single legal guardian, Parent / Guardian 2 details are not required.</Trans>
          </Alert>
        </Grid2>
      )}
    </Grid2>
  );
};
